import React, { FormEvent, useState } from 'react';
import { Button } from 'primereact/button';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { DropdownField, InputTextField } from '@/components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import useToastNotification from '@/hooks/useToastNotification';

import { generateEir } from './generateEir';
import DownloadPdf from './DownloadPdf/DownloadPdf';
import styles from './Eir.module.scss';

export interface EirFormState {
  type: string | null;
  plateNumber: string;
  container: string;
}

interface EirFormErrors {
  type: string;
  plateNumber: string;
  container: string;
}

const Eir: React.FC = () => {
  const { t } = useTranslation('eir');
  const { showToast } = useToastNotification();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const documentTypes = [
    { name: t('emptyDelivery'), value: 'ENTREGA VACIO' },
    { name: t('fullDelivery'), value: 'ENTREGA LLENO' },
    { name: t('emptyReception'), value: 'RECEPCION VACIO' },
    { name: t('fullReception'), value: 'RECEPCION LLENO' },
  ];

  const [eirWasSent, setEirWasSent] = useState(false);

  const [formData, setFormData] = useState<EirFormState>({
    type: 'ENTREGA VACIO',
    plateNumber: '',
    container: '',
  });

  const [formErrors, setFormErrors] = useState<EirFormErrors>({
    type: '',
    plateNumber: '',
    container: '',
  });

  const [pdfData, setPdfData] = useState<string | null>(null);

  const generateAnotherPdf = () => {
    setFormData({
      type: '',
      plateNumber: '',
      container: '',
    });
    setEirWasSent(false);
  };

  const generateEirMutation = useMutation({
    mutationFn: generateEir,
    onSuccess: (response) => {
      setPdfData(response.data.value);
      setEirWasSent(true);
    },
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('eirFormValidations.unexpectedError'),
        });
      }
      generateAnotherPdf();
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: string
  ) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };
  const validateForm = () => {
    let errors: EirFormErrors = { ...formErrors };

    if (!formData.container) {
      errors = {
        ...errors,
        container: t('eirFormValidations.containerRequired'),
      };
    }
    if (!formData.plateNumber) {
      errors = {
        ...errors,
        plateNumber: t('eirFormValidations.plateNumberRequired'),
      };
    }

    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    if (!executeRecaptcha) {
      showToast({
        severity: 'error',
        detail: t('eirFormValidations.reCaptcha'),
      });
      return;
    }

    const reCaptchaToken = await executeRecaptcha();

    if (!reCaptchaToken) {
      showToast({
        severity: 'error',
        detail: t('eirFormValidations.reCaptcha'),
      });
      return;
    }

    if (validateForm()) {
      const payload = {
        ...formData,
        docID: '',
      };
      generateEirMutation.mutate(payload);
    }
  };

  const handleDropdownChange = (e: { value: string | null }, name: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
  };

  const downloadPdf = () => {
    if (!pdfData) return;

    const byteCharacters = atob(pdfData.replace(/-/g, '+').replace(/_/g, '/'));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${formData.container}.pdf`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className={styles.container}>
      <div className={styles.whiteBg}>
        <form onSubmit={handleSubmit} className={styles.eirForm}>
          <header>
            <div>
              <h3>{t('downloadEIR')}</h3>
              <p className={styles.subtitle}>
                {t('completeFieldsForEIRDownload')}
              </p>
            </div>
            <div />
          </header>

          <InputTextField
            id="container"
            name="container"
            value={formData.container}
            onChange={(e) => handleInputChange(e, 'container')}
            placeholder={t('exampleContainer')}
            label={t('container')}
            error={formErrors.container}
          />

          <InputTextField
            id="plateNumber"
            name="plateNumber"
            value={formData.plateNumber}
            onChange={(e) => handleInputChange(e, 'plateNumber')}
            placeholder={t('exampleLicensePlate')}
            label={t('licensePlate')}
            error={formErrors.plateNumber}
          />

          <DropdownField
            id="type"
            label={t('deliveryReception')}
            options={documentTypes}
            value={formData.type}
            onChange={(e) => handleDropdownChange(e, 'type')}
            error={formErrors.type}
            className={styles.dropdown}
          />

          <Button
            type="submit"
            className={styles.submitButton}
            severity="success"
            disabled={generateEirMutation.isPending}
          >
            {t('downloadButton')}
          </Button>
          {eirWasSent && (
            <DownloadPdf
              generateAnotherPdf={generateAnotherPdf}
              downloadPdf={downloadPdf}
            />
          )}
        </form>
      </div>
    </div>
  );
};
export default Eir;
